import React from 'react';
import { cn } from '../../utils/cn';
import { LoadingSpinner } from './LoadingSpinner';

/**
 * Canonical button — ONE place for the monochrome-first variants + sizes that were previously
 * copy-pasted as inline Tailwind across 40+ call sites. Primary is near-black (white on dark), per
 * the design rules; brand colour is never baked in here. Pass any extra layout via `className`.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-secondary-900 text-white hover:bg-black dark:bg-white dark:text-secondary-900 dark:hover:bg-secondary-100',
  secondary: 'border border-secondary-300 text-secondary-700 hover:bg-secondary-50 dark:border-secondary-700 dark:text-secondary-200 dark:hover:bg-secondary-800',
  ghost: 'text-secondary-600 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-800',
  danger: 'bg-error-600 text-white hover:bg-error-700',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'px-2.5 py-1.5 text-xs gap-1.5',
  md: 'px-4 py-2 text-sm gap-2',
  lg: 'px-5 py-2.5 text-sm gap-2',
};

const SPINNER_SIZE: Record<ButtonSize, 'xs' | 'sm'> = { sm: 'xs', md: 'sm', lg: 'sm' };

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Show a spinner + disable while an action is in flight. */
  loading?: boolean;
  /** Optional leading icon (a Lucide element). Hidden while loading. */
  icon?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', loading = false, icon, disabled, className, children, ...rest }, ref) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center rounded-lg font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
        VARIANT[variant],
        SIZE[size],
        className,
      )}
      {...rest}
    >
      {loading ? <LoadingSpinner size={SPINNER_SIZE[size]} color={variant === 'secondary' || variant === 'ghost' ? 'secondary' : 'white'} /> : icon}
      {children}
    </button>
  ),
);

Button.displayName = 'Button';

export default Button;
